"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import MobileNavSearch from "./MobileNavSearch";
import { getMerchantHref, getEventsHref, getPromotionHref } from "@/constants/hooks";
import { Menu, X, ChevronRight, ArrowRight } from "lucide-react";

const MobileNavMenu = ({
  company_id, mer_slug, slug_type, cat_slug,
  categories, merchants, events, promotions,
  promotion_slug, blog_url, blog_title, companyLogo, company_name,
}: any) => {
  const [open, setOpen]           = useState(false);
  const [expanded, setExpanded]   = useState<string | null>(null);
  const pathname                  = usePathname();
  const drawerRef                 = useRef<HTMLDivElement>(null);

  /* lock body scroll */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  /* close on route change */
  useEffect(() => { setOpen(false); }, [pathname]);

  /* close on outside click */
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const close = () => { setOpen(false); setExpanded(null); };

  const navGroups = [
    { id: "stores",     label: "Stores",      path: "/all-stores/A", items: merchants,  type: "mer"   },
    { id: "categories", label: "Categories",  path: "/category",     items: categories, type: "cat"   },
    { id: "products",   label: "Products",    path: "/all-products", items: [{ id: "p1", name: "Branded Products", slug: "products" }], type: "prod"  },
    ...(events?.length     > 0 ? [{ id: "events",     label: "Events",     path: "/events",             items: events,     type: "event" }] : []),
    ...(promotions?.length > 0 ? [{ id: "promotions", label: "Promotions", path: `/${promotion_slug}`,  items: promotions, type: "promo" }] : []),
  ];

  const getHref = (type: string, item: any) => {
    if (type === "cat")   return `/${item.url}`;
    if (type === "mer")   return getMerchantHref(item, mer_slug, slug_type);
    if (type === "event") return getEventsHref(item, slug_type);
    if (type === "promo") return getPromotionHref(item, promotion_slug);
    if (type === "prod")  return `/${item.slug}`;
    return "/all-products";
  };

  return (
    <div className="lg:hidden">

      {/* ── Burger ── */}
      <button
        onClick={() => setOpen(true)}
        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 border border-white/15 text-white hover:bg-white/20 active:scale-90 transition-all"
        aria-label="Open menu"
      >
        <Menu size={20} strokeWidth={2} />
      </button>

      {/* ── Backdrop ── */}
      <div
        onClick={close}
        className={`fixed inset-0 z-[9990] bg-indigo-950/80 backdrop-blur-sm transition-opacity duration-300
          ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      {/* ── Drawer ── */}
      <div
        ref={drawerRef}
        className={`fixed top-0 left-0 h-[100dvh] w-[85vw] max-w-[340px] z-[9999]
          bg-indigo-950 flex flex-col shadow-[20px_0_60px_rgba(0,0,0,0.4)]
          transition-transform duration-400 ease-in-out
          ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* top accent line */}
        <div className="w-full h-[2px] bg-gradient-to-r from-blue-600 via-indigo-400 to-blue-600 shrink-0" />

        {/* ── Drawer header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08] shrink-0">
          <Link href="/" onClick={close}>
            <Image
              src={companyLogo || "https://gettopdiscounts.com/uploads/company_5/images/1747949529_682f97d92d00a.svg"}
              alt="Logo" width={130} height={40}
              className="h-8 w-auto object-contain brightness-0 invert"
              priority
            />
          </Link>
          <button
            onClick={close}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 border border-white/10 text-white/70 hover:text-white hover:bg-white/15 active:scale-90 transition-all"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* ── Search ── */}
        <div className="px-4 pt-4 pb-2 shrink-0">
          <MobileNavSearch
            companyId={company_id}
            mer_slug={mer_slug}
            slug_type={slug_type}
            cat_slug={cat_slug}
            closeDrawer={close}
          />
        </div>

        {/* ── Nav items ── */}
        <nav className="flex-1 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: "none" }}>

          {/* Home */}
          <Link
            href="/"
            onClick={close}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl no-underline mb-1 transition-colors
              ${pathname === "/" ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/[0.06]"}`}
          >
            <span className="text-[15px] font-semibold">Home</span>
            {pathname === "/" && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400" />}
          </Link>

          {/* Groups */}
          {navGroups.map((group) => {
            const isActive =
              group.path === "/"
                ? pathname === "/"
                : pathname === group.path || pathname.startsWith(group.path + "/") || pathname.startsWith("/" + group.id);

            return (
            <div key={group.id} className="mb-1">
              <button
                onClick={() => {
                  if (group.items.length === 0) { close(); return; }
                  setExpanded(expanded === group.id ? null : group.id);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-left
                  ${expanded === group.id || isActive ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/[0.06]"}`}
              >
                <span className="text-[15px] font-semibold flex-1">{group.label}</span>
                {group.items.length > 0 ? (
                  <ChevronRight
                    size={14} strokeWidth={2.5}
                    className={`text-white/40 transition-transform duration-200 ${expanded === group.id ? "rotate-90 text-blue-400" : isActive ? "text-blue-400" : ""}`}
                  />
                ) : (
                  <ArrowRight size={14} strokeWidth={2.5} className="text-white/30" />
                )}
              </button>

              {/* Expanded sub-items */}
              {expanded === group.id && group.items.length > 0 && (
                <div className="mt-1 ml-2 pl-3 border-l-2 border-indigo-800/60 space-y-0.5 pb-2">
                  {group.items.slice(0, 10).map((item: any, i: number) => (
                    <Link
                      key={i}
                      href={getHref(group.type, item)}
                      onClick={close}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg no-underline text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors"
                    >
                      <span className="w-1 h-1 rounded-full bg-white/25 shrink-0" />
                      <span className="text-[13px] font-medium truncate">
                        {item.name || item.merchant_name}
                      </span>
                    </Link>
                  ))}
                  <Link
                    href={group.path}
                    onClick={close}
                    className="flex items-center gap-1.5 px-3 py-2 no-underline text-blue-400 hover:text-blue-300 text-[11px] font-black uppercase tracking-widest transition-colors"
                  >
                    View all <ArrowRight size={11} strokeWidth={2.5} />
                  </Link>
                </div>
              )}
            </div>
            );
          })}

          {/* Blog — only render if blog_url comes from the API */}
          {blog_url && (
            <Link
              href={blog_url}
              target="_blank"
              onClick={close}
              className="flex items-center gap-3 px-4 py-3 rounded-xl no-underline text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors mb-1"
            >
              <span className="text-[15px] font-semibold">{blog_title || "Blog"}</span>
              <ArrowRight size={13} strokeWidth={2} className="ml-auto text-white/30" />
            </Link>
          )}

        </nav>

        {/* ── Footer ── */}
        <div className="px-5 py-4 border-t border-white/[0.08] shrink-0">
          {/* company name */}
          {company_name && (
            <p className="text-center text-white/70 text-[13px] font-semibold mb-1 tracking-tight">
              {company_name}
            </p>
          )}
          <p className="text-center text-[9px] text-white/20 font-bold tracking-[0.25em] uppercase">
            © {new Date().getFullYear()} All Rights Reserved
          </p>
        </div>

      </div>

      <style jsx global>{`
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default MobileNavMenu;
